import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import { useCartStore } from '../store/useCartStore';
import { createOrder, getReferralCodeInfo } from '../lib/api';
import { formatPrice } from '../lib/formatPrice';
import { usePaymentSettings } from '../lib/usePaymentSettings';
import { getStoredReferralCode, getStoredDealerCode, setStoredReferralCode, setStoredDealerCode, clearAttribution } from '../lib/referralStorage';
import { ShoppingCart, ChevronLeft, Trash2, Plus, Minus, Loader2, AlertCircle, CheckCircle2, CheckCircle, XCircle } from 'lucide-react';

const CheckoutPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, total, clearCart } = useCartStore();
  const { settings: paymentSettings } = usePaymentSettings();
  const priceOptions = {
    currencySymbol: paymentSettings.currencySymbol,
    locale: paymentSettings.priceLocale,
  };

  const [form, setForm] = useState({
    customerName: '',
    customerPhoneCountryCode: '852',
    customerPhoneNumber: '',
    customerEmail: '',
    shippingAddress: '',
    remark: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Referral/dealer attribution (Phase 6) — pre-filled from a /r/:code visit
  const [referralCode, setReferralCode] = useState('');
  const [dealerCode, setDealerCode] = useState('');
  const [referralValid, setReferralValid] = useState<boolean | null>(null);

  useEffect(() => {
    setReferralCode(getStoredReferralCode());
    setDealerCode(getStoredDealerCode());
  }, []);

  // Validate the referral code on change (debounced) so the customer knows
  // whether attribution will apply. Invalid codes are dropped server-side too.
  useEffect(() => {
    const code = referralCode.trim();
    if (!code) {
      setReferralValid(null);
      return;
    }
    setReferralValid(null);
    const timer = setTimeout(async () => {
      const res = await getReferralCodeInfo(code);
      setReferralValid(Boolean(res.data?.valid));
    }, 500);
    return () => clearTimeout(timer);
  }, [referralCode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const fullPhoneNumber = `${form.customerPhoneCountryCode.replace(/\D/g, '')}${form.customerPhoneNumber.replace(/\D/g, '')}`.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const res = await createOrder({
        customerName: form.customerName.trim(),
        customerPhone: fullPhoneNumber,
        customerPhoneCountryCode: form.customerPhoneCountryCode.replace(/\D/g, '') || '852',
        customerEmail: form.customerEmail.trim() || undefined,
        shippingAddress: form.shippingAddress.trim() || undefined,
        remark: form.remark.trim() || undefined,
        items: items.map((item) => ({
          productId: item.productId,
          variantId: item.variantId,
          quantity: item.quantity,
        })),
        totalAmount: total(),
        referralCode: referralCode.trim() || undefined,
        dealerCode: dealerCode.trim() || undefined,
      });

      if (!res.success || !res.data) {
        throw new Error(res.error?.message || 'Failed to create order');
      }

      clearCart();
      clearAttribution();
      navigate(`/order/${res.data.orderNum}/confirm`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-paper">
      <Header />
      <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-base text-brand-ink/60 hover:text-brand-cinnabar"
            >
              <ChevronLeft className="w-5 h-5" />
              繼續購物
            </Link>
          </div>

          <h1 className="text-3xl md:text-4xl font-serif tracking-widest text-brand-ink mb-8">
            {t('checkout.title')}
          </h1>

          {items.length === 0 ? (
            <div className="bg-white border border-brand-ink/10 p-10 text-center">
              <ShoppingCart className="w-16 h-16 text-brand-ink/20 mx-auto mb-4" />
              <p className="text-xl text-brand-ink/60 mb-6">{t('cart.empty')}</p>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-cinnabar text-brand-paper text-lg font-medium hover:bg-brand-ink transition-colors"
              >
                瀏覽產品
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Cart summary */}
              <div className="space-y-6">
                <div className="bg-white border border-brand-ink/10 p-6">
                  <h2 className="text-xl font-serif text-brand-ink mb-6">訂單明細</h2>
                  <div className="space-y-6">
                    {items.map((item) => (
                      <div
                        key={`${item.productId}-${item.variantId ?? 'none'}`}
                        className="flex gap-4 pb-6 border-b border-brand-ink/5 last:border-0 last:pb-0"
                      >
                        <div className="w-24 h-24 aspect-square bg-brand-stone/30 flex-shrink-0">
                          {item.image ? (
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-brand-ink/20">
                              <ShoppingCart className="w-8 h-8" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-serif text-brand-ink mb-1">{item.name}</h3>
                          <p className="text-brand-ink/50 mb-3">{formatPrice(item.price, priceOptions)} / 件</p>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => updateQuantity(item.productId, item.variantId, item.quantity - 1)}
                              className="w-10 h-10 border border-brand-ink/10 flex items-center justify-center hover:bg-brand-stone"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-10 text-center text-lg font-medium">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.productId, item.variantId, item.quantity + 1)}
                              className="w-10 h-10 border border-brand-ink/10 flex items-center justify-center hover:bg-brand-stone"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-col items-end justify-between gap-4">
                          <span className="text-2xl font-bold text-brand-cinnabar">
                            {formatPrice(item.price * item.quantity, priceOptions)}
                          </span>
                          <button
                            onClick={() => removeItem(item.productId, item.variantId)}
                            className="p-2 text-brand-ink/40 hover:text-brand-cinnabar"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 pt-6 border-t border-brand-ink/10 flex items-center justify-between">
                    <span className="text-xl text-brand-ink">{t('cart.total')}</span>
                    <span className="text-3xl font-bold text-brand-cinnabar">{formatPrice(total(), priceOptions)}</span>
                  </div>
                </div>
              </div>

              {/* Checkout form */}
              <div className="bg-white border border-brand-ink/10 p-6 md:p-8">
                <h2 className="text-xl font-serif text-brand-ink mb-6">收件資料</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm text-brand-ink/70 mb-2">{t('checkout.name')} *</label>
                    <input
                      type="text"
                      name="customerName"
                      required
                      value={form.customerName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-brand-ink/10 focus:border-brand-cinnabar focus:outline-none bg-brand-paper"
                      placeholder="請輸入姓名"
                    />
                  </div>

                  <div>
                    <div className="flex gap-3">
                      <div className="w-24 flex-shrink-0">
                        <span className="text-sm text-brand-ink/50 mb-1.5 block">區號</span>
                        <input
                          type="tel"
                          name="customerPhoneCountryCode"
                          required
                          value={form.customerPhoneCountryCode}
                          onChange={handleChange}
                          className="w-full px-3 py-3 border border-brand-ink/10 focus:border-brand-cinnabar focus:outline-none bg-brand-paper text-center"
                          placeholder="852"
                        />
                      </div>
                      <div className="flex-1">
                        <span className="text-sm text-brand-ink/50 mb-1.5 block">WhatsApp 號碼</span>
                        <input
                          type="tel"
                          name="customerPhoneNumber"
                          required
                          value={form.customerPhoneNumber}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-brand-ink/10 focus:border-brand-cinnabar focus:outline-none bg-brand-paper"
                          placeholder="例如：9123 4567"
                        />
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-brand-ink/40">此號碼將用於接收訂單 WhatsApp 通知</p>
                  </div>

                  <div>
                    <label className="block text-sm text-brand-ink/70 mb-2">{t('checkout.email')}</label>
                    <input
                      type="email"
                      name="customerEmail"
                      value={form.customerEmail}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-brand-ink/10 focus:border-brand-cinnabar focus:outline-none bg-brand-paper"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-brand-ink/70 mb-2">{t('checkout.address')}</label>
                    <input
                      type="text"
                      name="shippingAddress"
                      value={form.shippingAddress}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-brand-ink/10 focus:border-brand-cinnabar focus:outline-none bg-brand-paper"
                      placeholder="請輸入送貨地址"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-brand-ink/70 mb-2">{t('checkout.remark')}</label>
                    <textarea
                      name="remark"
                      rows={3}
                      value={form.remark}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-brand-ink/10 focus:border-brand-cinnabar focus:outline-none bg-brand-paper resize-none"
                      placeholder="如有特殊要求請在此說明"
                    />
                  </div>

                  {/* Referral / dealer attribution (optional) */}
                  <div className="border-t border-brand-ink/10 pt-4 space-y-3">
                    <div>
                      <label className="block text-sm text-brand-ink/70 mb-2">
                        {t('checkout.referralCode', '推薦碼（選填）')}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={referralCode}
                          onChange={(e) => {
                            const v = e.target.value.toUpperCase();
                            setReferralCode(v);
                            setStoredReferralCode(v);
                          }}
                          className="w-full px-4 py-3 pr-10 border border-brand-ink/10 focus:border-brand-cinnabar focus:outline-none bg-brand-paper font-mono uppercase"
                          placeholder="FYXXXXXX"
                        />
                        {referralValid !== null && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2">
                            {referralValid ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-amber-500" />
                            )}
                          </span>
                        )}
                      </div>
                      {referralValid === false && (
                        <p className="mt-1 text-xs text-amber-600">
                          {t('checkout.referralInvalid', '推薦碼無效，此欄將被忽略')}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm text-brand-ink/70 mb-2">
                        {t('checkout.dealerCode', '經銷商代碼（選填）')}
                      </label>
                      <input
                        type="text"
                        value={dealerCode}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase();
                          setDealerCode(v);
                          setStoredDealerCode(v);
                        }}
                        className="w-full px-4 py-3 border border-brand-ink/10 focus:border-brand-cinnabar focus:outline-none bg-brand-paper font-mono uppercase"
                        placeholder="FYXXXXXX"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-brand-cinnabar text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-5 text-xl font-medium bg-green-600 text-brand-paper rounded-full hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-md hover:shadow-lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        處理中…
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        {t('checkout.submit')}
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <FloatingActions />
    </div>
  );
};

export default CheckoutPage;
